import { Component, ElementRef, inject, Input, NgZone, OnDestroy, OnInit, PLATFORM_ID, signal, ViewChild } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { distinctUntilChanged, Subscription, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ApiService } from '../../../services/api.service';
import { ThemeService } from '../../../services/theme.service';
import { FsTicketsAuthService } from '../../../services/fs-tickets/fs-tickets-auth.service';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

@Component({
  selector: 'app-fs-tickets-embed',
  standalone: true,
  imports: [HeaderComponent, FooterComponent, ProgressSpinnerModule],
  templateUrl: './fs-tickets-embed.component.html',
  styleUrl: './fs-tickets-embed.component.scss'
})
export class FsTicketsEmbedComponent implements OnInit, OnDestroy {
  @Input() id!: number;
  @ViewChild('fsIframe') private iframeRef!: ElementRef<HTMLIFrameElement>;

  iframeUrl: SafeResourceUrl | null = null;
  isLoading = signal(true);
  errorMessage = signal<string | null>(null);

  private static readonly DEFAULT_ERROR_MESSAGE = 'Online Ticket Sales are unavailable';

  private fsOrigin = environment.fsTicketsOrigin;
  private lastContentHeight: number | null = null;
  private iframeReady = false;
  private currentToken = '';
  private cartSubscription?: Subscription;
  private tokenSubscription?: Subscription;
  private readonly messageHandler = (event: MessageEvent) => this.onMessage(event);

  private readonly platformId = inject(PLATFORM_ID);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly router = inject(Router);
  private readonly apiService = inject(ApiService);
  private readonly themeService = inject(ThemeService);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly fsTicketsAuthService = inject(FsTicketsAuthService);
  private readonly zone = inject(NgZone);

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.themeService.customizeAuctionTheme(this.id);

    const existing = this.apiService.auctionConfigSignal();
    if (existing) {
      this.applyConfig(existing.ticketSalesMode as string);
    } else {
      this.apiService.getAuctionConfig(this.id).subscribe(config => {
        this.applyConfig(config.ticketSalesMode as string);
      });
    }
  }

  private applyConfig(ticketSalesMode: string): void {
    if (ticketSalesMode !== 'FS_TICKETS') {
      this.router.navigate(['/ticket-event', this.id], { replaceUrl: true });
      return;
    }

    this.cartSubscription = this.apiService.createFsTicketsCart(this.id).subscribe({
      next: ({ cartId, browserSessionId }) => {
        const cartUrl = `${environment.fsTicketsOrigin}/checkout/${cartId}/tickets?session=${browserSessionId}`;
        this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(cartUrl);
        this.isLoading.set(false);

        this.tokenSubscription = this.oidcSecurityService.getAccessToken()
          .pipe(distinctUntilChanged())
          .subscribe(token => {
            if (!token) return;
            const prev = this.currentToken;
            this.currentToken = token;
            if (this.iframeReady && prev && prev !== token) {
              this.postAuthToken(token);
            }
          });

        this.zone.runOutsideAngular(() => window.addEventListener('message', this.messageHandler));
      },
      error: (err: HttpErrorResponse) => {
        const message = err.status === 409 ? err.error?.message : null;
        this.errorMessage.set(message || FsTicketsEmbedComponent.DEFAULT_ERROR_MESSAGE);
        this.isLoading.set(false);
      }
    });
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('message', this.messageHandler);
    }
    this.cartSubscription?.unsubscribe();
    this.tokenSubscription?.unsubscribe();
  }

  private onMessage(event: MessageEvent): void {
    if (event.origin !== this.fsOrigin) return;
    const msg = event.data;
    switch (msg?.type) {
      case 'FS_READY':
        this.zone.run(() => {
          this.iframeReady = true;
          this.postAuthToken(this.currentToken);
        });
        break;
      case 'FS_HEIGHT_CHANGE':
        if (typeof msg.height === 'number' && msg.height !== this.lastContentHeight) {
          const iframe = this.iframeRef?.nativeElement;
          if (iframe) {
            this.lastContentHeight = msg.height;
            iframe.style.height = `${msg.height}px`;
          }
        }
        break;
      case 'FS_SCROLL_TO_TOP':
        this.iframeRef?.nativeElement?.scrollIntoView({ block: 'start' });
        break;
      case 'FS_SESSION_EXPIRED':
        this.fsTicketsAuthService.refreshAccessToken().pipe(take(1)).subscribe(token => {
          this.zone.run(() => {
            if (token) {
              this.currentToken = token;
              this.postAuthToken(token);
            } else {
              window.open(environment.mediaUrl, '_self');
            }
          });
        });
        break;
    }
  }

  private postAuthToken(token: string): void {
    if (!token) return;
    this.iframeRef?.nativeElement?.contentWindow?.postMessage(
      { type: 'FS_AUTH_TOKEN', sessionToken: token, actorType: 'customer_user' },
      this.fsOrigin
    );
  }
}
